import {
  CaretRightOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  Card,
  Collapse,
  Divider,
  Flex,
  Spin,
  Tag,
  Tooltip,
} from "antd";
import Text from "antd/es/typography/Text";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "../../../api";
import Colors from "../../../assets/styles/Colors";
import Loader from "../../../components/Loader";
import { numberWithSpaces } from "../../../domain/types";
import { useAppSelector } from "../../../store";
import { AppState } from "../../../store/appSlice";
import React from "react";

const STORAGE_KEYS = {
  SELECTED_COMPANY_ID: "admin_products_selected_company_id",
  SELECTED_COUNTRY_INDEX: "admin_products_selected_country_index",
};

const AdminProducts = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { field_Language } = useAppSelector(
    (data: { app: AppState }) => data.app
  );

  const [selectedCompanyID, setSelectedCompanyID] = useState<
    number | undefined
  >(() => {
    const saved = sessionStorage.getItem(STORAGE_KEYS.SELECTED_COMPANY_ID);
    return saved ? Number.parseInt(saved, 10) : undefined;
  });

  const [activeCountryKey, setActiveCountryKey] = useState<string | string[]>(
    () => {
      const saved = sessionStorage.getItem(STORAGE_KEYS.SELECTED_COUNTRY_INDEX);
      return saved || "";
    }
  );

  // get product list by id
  const { data: ProductListById, isLoading: isProductListByIdLoading } =
    api.useGetProductListByCategoryIdQuery(selectedCompanyID as number, {
      skip: !selectedCompanyID,
    });

  // company list
  const { data: companyList, isLoading: isCompanyListLoading } =
    api.useCompanyListQuery(undefined);

  const { data: countries, isLoading: isCountriesLoading } =
    api.useGetCountriesQuery(undefined);

  const groupedByCountry = useMemo(() => {
    if (!countries?.length || !companyList?.length) return [];

    return countries
      .map((country) => {
        const matchedCompanies = companyList.filter(
          (company) => company.countryCode === country.isoCode
        );
        if (matchedCompanies.length === 0) return null;

        return {
          country: {
            isoCode: country.isoCode,
            image: country.iconUrl,
            name: country.translations?.[field_Language]?.name || country.name,
          },
          companies: matchedCompanies,
        };
      })
      .filter(Boolean);
  }, [countries, companyList, field_Language]);

  // Restore state from sessionStorage when data is loaded
  useEffect(() => {
    if (
      groupedByCountry.length > 0 &&
      selectedCompanyID &&
      activeCountryKey === ""
    ) {
      // Find which country contains the selected company
      const countryIndex = groupedByCountry.findIndex((group) =>
        group?.companies.some((company) => company.id === selectedCompanyID)
      );

      if (countryIndex !== -1) {
        const countryKey = String(countryIndex);
        setActiveCountryKey(countryKey);
        sessionStorage.setItem(STORAGE_KEYS.SELECTED_COUNTRY_INDEX, countryKey);
      }
    }
  }, [groupedByCountry, selectedCompanyID, activeCountryKey]);

  // Save to sessionStorage when company is selected
  const handleCompanySelect = (companyId: number, countryIndex: number) => {
    setSelectedCompanyID(companyId);
    setActiveCountryKey(String(countryIndex));
    sessionStorage.setItem(STORAGE_KEYS.SELECTED_COMPANY_ID, String(companyId));
    sessionStorage.setItem(
      STORAGE_KEYS.SELECTED_COUNTRY_INDEX,
      String(countryIndex)
    );
  };

  // Handle accordion change
  const handleAccordionChange = (key: string | string[]) => {
    setActiveCountryKey(key);

    let countryIndex = "";
    if (typeof key === "string") {
      countryIndex = key;
    } else if (Array.isArray(key) && key.length > 0) {
      countryIndex = key[0];
    }

    if (countryIndex) {
      sessionStorage.setItem(STORAGE_KEYS.SELECTED_COUNTRY_INDEX, countryIndex);
    } else {
      // Clear country index when accordion is closed
      sessionStorage.removeItem(STORAGE_KEYS.SELECTED_COUNTRY_INDEX);
    }
  };

  return (
    <>
      {isCompanyListLoading || isCountriesLoading ? (
        <Loader />
      ) : (
        <>
          <Flex gap={30} vertical>
            <Collapse
              accordion
              size="large"
              activeKey={activeCountryKey}
              onChange={handleAccordionChange}
              items={groupedByCountry.map((item, index) => ({
                key: index,
                label: (
                  <Flex justify="space-between">
                    <img
                      style={{ height: 25, width: 25, objectFit: "contain" }}
                      src={item?.country.image}
                      alt="flag"
                    />
                    {item?.country?.name}
                  </Flex>
                ),
                children: (
                  <>
                    <Flex gap={20} wrap>
                      {item?.companies?.map((company) => (
                        <Tooltip
                          key={company.id}
                          title={company.brandName?.[field_Language]?.title}
                        >
                          <Card
                            size="small"
                            hoverable
                            onClick={() =>
                              handleCompanySelect(company.id, index)
                            }
                            style={{
                              width: "65px",
                              height: "65px",
                              outline:
                                company.id === selectedCompanyID
                                  ? "2px solid #1677ff"
                                  : "none",
                            }}
                          >
                            {isProductListByIdLoading &&
                            selectedCompanyID === company.id ? (
                              <Spin
                                indicator={<LoadingOutlined spin />}
                                size="large"
                              />
                            ) : (
                              <Avatar
                                shape="square"
                                size="large"
                                src={
                                  <img
                                    src={company?.logo}
                                    alt={`${company?.logo}-alt`}
                                    style={{ objectFit: "contain" }}
                                  />
                                }
                              />
                            )}
                          </Card>
                        </Tooltip>
                      ))}
                    </Flex>

                    {ProductListById &&
                      item?.companies.find(
                        (x) => x.id === selectedCompanyID
                      ) && (
                        <>
                          <Divider dashed />
                          <Flex
                            vertical
                            gap={15}
                            wrap="wrap"
                            justify="space-evenly"
                          >
                            {ProductListById.map((item, index) => (
                              <Collapse
                                key={index}
                                size="middle"
                                items={[
                                  {
                                    key: index,
                                    label: (
                                      <Flex justify="space-between">
                                        <span>
                                          {item?.productCategory?.title}
                                        </span>

                                        <Tooltip
                                          title={
                                            item?.integrated
                                              ? t("integrated")
                                              : t("nonIntegrated")
                                          }
                                        >
                                          {item?.integrated ? (
                                            <CheckCircleOutlined
                                              style={{ color: Colors.java }}
                                            />
                                          ) : (
                                            <CloseCircleOutlined
                                              style={{
                                                color: Colors.sunsetOrange,
                                              }}
                                            />
                                          )}
                                        </Tooltip>
                                      </Flex>
                                    ),
                                    extra: (
                                      <Tooltip title={t("editProduct")}>
                                        <SettingOutlined
                                          onClick={() => {
                                            let path = "/";
                                            if (
                                              item.targetType ===
                                              "TravelProduct"
                                            ) {
                                              path = `/travel-product-edit/${item.targetId}`;
                                            } else if (
                                              item.targetType === "TplProduct"
                                            ) {
                                              path = `/tpl-product-edit/${item.targetId}`;
                                            } else if (
                                              item.targetType ===
                                              "WelcomerProduct"
                                            ) {
                                              path = `/welcomer-product-edit/${item.targetId}`;
                                            } else if (
                                              item.targetType === "PetProduct"
                                            ) {
                                              path = `/pet-product-edit/${item.targetId}`;
                                            } else if (
                                              item.targetType ===
                                              "InternationalTplProduct"
                                            ) {
                                              path = `/international-tpl-product-edit/${item.targetId}`;
                                            }

                                            navigate(path);
                                          }}
                                          style={{ marginLeft: "20px" }}
                                        />
                                      </Tooltip>
                                    ),
                                    children: (
                                      <Flex vertical gap={15}>
                                        {item?.packages?.map(
                                          (pkg, pkgIndex) => (
                                            <Collapse
                                              size="small"
                                              expandIcon={({ isActive }) => (
                                                <CaretRightOutlined
                                                  rotate={isActive ? 90 : 0}
                                                />
                                              )}
                                              key={pkgIndex}
                                              items={[
                                                {
                                                  key: pkgIndex,
                                                  label: (
                                                    <Flex justify="space-between">
                                                      {pkg?.title}

                                                      <Tooltip
                                                        title={
                                                          pkg?.status
                                                            ?.charAt(0)
                                                            ?.toUpperCase() +
                                                          pkg?.status?.slice(1)
                                                        }
                                                      >
                                                        <Tag
                                                          style={{
                                                            marginRight: 0,
                                                            textTransform:
                                                              "capitalize",
                                                            letterSpacing: 1,
                                                          }}
                                                          color={
                                                            pkg?.status ===
                                                            "draft"
                                                              ? "red"
                                                              : "green"
                                                          }
                                                        >
                                                          {pkg?.status}
                                                        </Tag>
                                                      </Tooltip>
                                                    </Flex>
                                                  ),
                                                  children: (
                                                    <Flex vertical gap={10}>
                                                      {item?.productCategoryId ===
                                                        1 && (
                                                        <Flex gap={20}>
                                                          <Text
                                                            style={{
                                                              minWidth: "150px",
                                                            }}
                                                          >
                                                            {t("multiPack")}:
                                                          </Text>
                                                          <Text>
                                                            {pkg.target
                                                              ?.enableMulti
                                                              ? `${t("yes")}`
                                                              : `${t("no")}`}
                                                          </Text>
                                                        </Flex>
                                                      )}

                                                      {item?.productCategoryId ===
                                                        1 && (
                                                        <Flex gap={20}>
                                                          <Text
                                                            style={{
                                                              minWidth: "150px",
                                                            }}
                                                          >
                                                            {t("area")}:
                                                          </Text>
                                                          <Text>
                                                            {/* @ts-ignore */}
                                                            {pkg.target?.areas
                                                              ?.map(
                                                                (a) => a.area
                                                              )
                                                              .join(", ")}
                                                          </Text>
                                                        </Flex>
                                                      )}

                                                      <Flex gap={20}>
                                                        <Text
                                                          style={{
                                                            minWidth: "150px",
                                                          }}
                                                        >
                                                          {t("limit")}:
                                                        </Text>
                                                        <Text>
                                                          {numberWithSpaces(
                                                            pkg.compensationLimit
                                                          )}{" "}
                                                        </Text>
                                                      </Flex>

                                                      <Flex gap={20}>
                                                        <Text
                                                          style={{
                                                            minWidth: "150px",
                                                          }}
                                                        >
                                                          {t("currency")}:
                                                        </Text>
                                                        <Text>
                                                          {pkg?.compensationCurrencies
                                                            .map(
                                                              (c) => c.currency
                                                            )
                                                            .join(", ")}
                                                        </Text>
                                                      </Flex>

                                                      <Flex gap={20}>
                                                        <Text
                                                          style={{
                                                            minWidth: "150px",
                                                          }}
                                                        >
                                                          {t("activationDate")}:
                                                        </Text>
                                                        <Text>
                                                          {dayjs(
                                                            pkg.activatedDate
                                                          ).format(
                                                            "DD.MM.YYYY | HH:mm"
                                                          )}
                                                        </Text>
                                                      </Flex>
                                                    </Flex>
                                                  ),
                                                },
                                              ]}
                                            />
                                          )
                                        )}
                                        <Button
                                          size="large"
                                          type="primary"
                                          onClick={() => {
                                            let path = "/";
                                            if (
                                              item.targetType ===
                                              "TravelProduct"
                                            ) {
                                              path = `/travel-product-edit/${item.targetId}`;
                                            } else if (
                                              item.targetType === "TplProduct"
                                            ) {
                                              path = `/tpl-product-edit/${item.targetId}`;
                                            } else if (
                                              item.targetType ===
                                              "WelcomerProduct"
                                            ) {
                                              path = `/welcomer-product-edit/${item.targetId}`;
                                            } else if (
                                              item.targetType === "PetProduct"
                                            ) {
                                              path = `/pet-product-edit/${item.targetId}`;
                                            } else if (
                                              item.targetType ===
                                              "InternationalTplProduct"
                                            ) {
                                              path = `/international-tpl-product-edit/${item.targetId}`;
                                            }

                                            navigate(path);
                                          }}
                                        >
                                          {t("editProduct")}
                                        </Button>
                                      </Flex>
                                    ),
                                  },
                                ]}
                              />
                            ))}
                          </Flex>
                        </>
                      )}
                  </>
                ),
              }))}
            />
          </Flex>
        </>
      )}
    </>
  );
};

export default AdminProducts;
